// src/utils/teamAccess.js
//
// Pass 50A (items 10/11) — ONE canonical human identity. A person exists in the roster BEFORE
// having Auth. These pure helpers keep the SEPARATE axes visible: Motus ACCESS status vs.
// permission ROLE vs. capabilities vs. season participation vs. commercial category. They never
// conflate permission role with capability.

const str = (v) => String(v == null ? '' : v).trim();

export const ACCESS = { NOT_INVITED: 'not_invited', INVITE_PENDING: 'invite_pending', ACTIVE: 'active', DISABLED: 'disabled' };
export const ACCESS_LABEL = { not_invited: 'Not invited', invite_pending: 'Invite pending', active: 'Active', disabled: 'Disabled' };

// Motus app-access status for a member. Precedence: explicitly disabled → disabled; linked Auth
// (authUserId) → active; a pending invite (on the member or in the invites list) → invite_pending;
// else not_invited.
export function accessStatusOf(member = {}, pendingInviteEmails = new Set()) {
  if (member.accessDisabled === true || member.inviteStatus === 'disabled') return ACCESS.DISABLED;
  if (str(member.authUserId)) return ACCESS.ACTIVE;
  const email = str(member.email || member.contactEmail).toLowerCase();
  if (member.inviteStatus === 'pending' || (email && pendingInviteEmails.has(email))) return ACCESS.INVITE_PENDING;
  return ACCESS.NOT_INVITED;
}

// Organization PERMISSION role (owner/admin/ops/instructor/…) — distinct from work capability.
export function permissionRoleOf(member = {}) {
  const r = str(member.orgRole) || str(member.role);
  if (/owner/.test(r)) return 'organization_owner';
  if (/admin/.test(r)) return 'organization_admin';
  if (/^ops$|operations/.test(r)) return 'ops';   // Pass 58A: OPS is a distinct org role
  if (/instructor/.test(r)) return 'instructor';
  return r || 'member';
}
export const PERMISSION_LABEL = {
  organization_owner: 'Owner', organization_admin: 'Admin', ops: 'Ops',
  instructor: 'Instructor', member: 'Member',
};

// Pass 58A — the FIVE Team-access UI states. Splits the base `not_invited` into EMAIL_REQUIRED (no real
// stored email — an operator must supply one; never fabricate/guess) vs READY_TO_INVITE (a real email
// exists). Never synthesizes an address from a name or a domain.
export const TEAM_UI_STATE = { EMAIL_REQUIRED: 'email_required', READY_TO_INVITE: 'ready_to_invite', INVITE_PENDING: 'invite_pending', ACTIVE: 'active', DISABLED: 'disabled' };
export const TEAM_UI_STATE_LABEL = { email_required: 'Email required', ready_to_invite: 'Ready to invite', invite_pending: 'Invite pending', active: 'Active', disabled: 'Disabled' };

// A member has an approved, usable email ONLY if one is actually stored on the member doc. This never
// derives an address (e.g. from altitudegroupjapan.com) — a missing email is EMAIL_REQUIRED, full stop.
export function hasApprovedEmail(member = {}) {
  return /.+@.+\..+/.test(str(member.email || member.contactEmail));
}

export function teamAccessUiState(member = {}, pendingInviteEmails = new Set()) {
  const base = accessStatusOf(member, pendingInviteEmails);
  if (base === ACCESS.DISABLED) return TEAM_UI_STATE.DISABLED;
  if (base === ACCESS.ACTIVE) return TEAM_UI_STATE.ACTIVE;
  if (base === ACCESS.INVITE_PENDING) return TEAM_UI_STATE.INVITE_PENDING;
  return hasApprovedEmail(member) ? TEAM_UI_STATE.READY_TO_INVITE : TEAM_UI_STATE.EMAIL_REQUIRED;
}

// A person's canonical organizationMember doc id (deterministic — matches 49A / pilot script).
export function memberDocId(member = {}) {
  return member.id || (member.userId && member.organizationId ? `${member.userId}::${member.organizationId}` : '');
}
